import { StatusChip } from '../ui/primitives.js';
// /m/<id>: header, status and partial/failure notices, summary, transcript and
// delete. No media is served (spec v3.9): selecting a segment only highlights
// it. Unknown, deleted and unreadable meetings look alike.
import { useCallback, useEffect, useRef, useState } from 'react';
import { isAbort, isApiError, userMessage } from '../api/client.js';
import type { WebMeetingDetail, WebSegment, WebTrackStatus } from '../api/types.js';
import { COPY } from '../copy.js';
import { navigate, paths } from '../router.js';
import { useServices } from '../services.js';
import { renderJsonText } from '../summary/render.js';
import { formatDate, isProcessing, recordingUnavailableText, speakerLabel } from '../ui/labels.js';
import { useNarrow } from '../ui/useNarrow.js';
import { segmentAtMeetingMs, Transcript } from './Transcript.js';
import { usePaged } from './usePaged.js';

type DetailState =
  | { kind: 'loading' }
  | { kind: 'ok'; detail: WebMeetingDetail }
  | { kind: 'not_found' }
  | { kind: 'error'; message: string };

const FAILED_TRACK_STATES = new Set(['failed', 'transcription_failed', 'abandoned']);

function failedTracks(tracks: WebTrackStatus[]): string[] {
  return tracks
    .filter((t) => FAILED_TRACK_STATES.has(t.state))
    .map((t) => speakerLabel(t.shared_mic ? 'shared_mic' : 'person', t.participant));
}

/** `atMs` (from `?t=`): meeting-timeline ms whose loaded segment starts selected. */
export function MeetingPage({ id, atMs }: { id: string; atMs: number | null }) {
  const { api, confirm } = useServices();
  const narrow = useNarrow();
  const [state, setState] = useState<DetailState>({ kind: 'loading' });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [revealId, setRevealId] = useState<string | null>(null);
  /** The `?t=` value already applied (or overridden by the user); never fetches to resolve it. */
  const handledAt = useRef<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const segments = usePaged<WebSegment>(state.kind === 'ok' ? id : null, (cursor, signal) =>
    api.segments(id, { cursor }, signal),
  );

  useEffect(() => {
    const ctl = new AbortController();
    setState({ kind: 'loading' });
    api.meeting(id, ctl.signal).then(
      (detail) => setState({ kind: 'ok', detail }),
      (err: unknown) => {
        if (isAbort(err)) return;
        if (isApiError(err, 'meeting_not_found')) setState({ kind: 'not_found' });
        else setState({ kind: 'error', message: userMessage(err) });
      },
    );
    return () => ctl.abort();
  }, [api, id]);

  useEffect(() => {
    if (atMs === null || handledAt.current === atMs) return;
    const hit = segmentAtMeetingMs(segments.items, atMs);
    if (!hit) return;
    handledAt.current = atMs;
    setSelectedId(hit.id);
    setRevealId(hit.id);
  }, [atMs, segments.items]);

  const onSelect = useCallback(
    (s: WebSegment) => {
      handledAt.current = atMs;
      setSelectedId(s.id);
    },
    [atMs],
  );

  if (state.kind === 'loading') return <p className="page muted">{COPY.loading}</p>;
  if (state.kind === 'not_found' || state.kind === 'error') {
    return (
      <div className="page">
        <p className="error" role="alert">
          {state.kind === 'not_found' ? COPY.meetingNotFound : state.message}
        </p>
        <button type="button" onClick={() => navigate(paths.home())}>
          {COPY.backHome}
        </button>
      </div>
    );
  }

  const { meeting, summary, tracks } = state.detail;
  const noRecording = recordingUnavailableText(meeting.recording);
  const failed = failedTracks(tracks);

  const remove = () => {
    if (!confirm(COPY.deleteConfirm)) return;
    setDeleting(true);
    setDeleteError(null);
    api.deleteMeeting(meeting.id).then(
      () => navigate(paths.home()),
      (err: unknown) => {
        setDeleting(false);
        setDeleteError(userMessage(err));
      },
    );
  };

  const download = () => {
    setDownloading(true);
    setDownloadError(null);
    api.transcriptText(meeting.id).then(
      ({ filename, blob }) => {
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename;
        anchor.hidden = true;
        document.body.append(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(url);
        setDownloading(false);
      },
      (err: unknown) => {
        setDownloading(false);
        setDownloadError(userMessage(err));
      },
    );
  };

  const canDownload = segments.items.length > 0 || !isProcessing(meeting.status);

  return (
    <div className={narrow ? 'meeting meeting--narrow' : 'meeting'} data-testid="meeting-page">
      <header className="meeting__header">
        <h1>
          {COPY.meeting} {meeting.room ? meeting.room.title : ''} &#183; {formatDate(meeting.started_at)}
        </h1>
        <p className="muted">
          <StatusChip status={meeting.status} />
          {meeting.participants.length > 0 &&
            ` \u00b7 ${COPY.participants}: ${meeting.participants.map((p) => p.display_name).join(', ')}`}
        </p>
        <div className="meeting__actions">
          {canDownload && (
            <button type="button" className="button" onClick={download} disabled={downloading}>
              {downloading ? COPY.downloadingTranscript : COPY.downloadTranscript}
            </button>
          )}
          {meeting.can_delete && (
            <button type="button" className="button danger" onClick={remove} disabled={deleting}>
              {deleting ? COPY.deleting : COPY.deleteMeeting}
            </button>
          )}
        </div>
        {downloadError && (
          <p className="error" role="alert">
            {downloadError}
          </p>
        )}
        {deleteError && (
          <p className="error" role="alert">
            {deleteError}
          </p>
        )}
      </header>

      <div className="meeting__notices" data-testid="meeting-notices">
        {noRecording && <p className="notice">{noRecording}</p>}
        {isProcessing(meeting.status) && <p className="notice">{COPY.processing}</p>}
        {meeting.status === 'partial' && <p className="warning">{COPY.partial}</p>}
        {meeting.status === 'failed' && <p className="error">{COPY.failed}</p>}
        {failed.length > 0 && (
          <p className="warning">
            {COPY.failedTracks} {failed.join(', ')}
          </p>
        )}
      </div>

      <div className="meeting__body">
        <section aria-labelledby="summary-title" className="summary">
          <h2 id="summary-title">{COPY.summary}</h2>
          {summary ? (
            <div className="summary-text" data-testid="summary-text">
              {renderJsonText(summary.body)}
            </div>
          ) : (
            <p className="muted">{COPY.summaryEmpty}</p>
          )}
        </section>
        <Transcript segments={segments} selectedId={selectedId} onSelect={onSelect} revealId={revealId} />
      </div>
    </div>
  );
}
